import type { HTMLAttributes, ReactNode } from 'react';

import { cx } from '../cx';

export type CardProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  /** Numero da secao ou do passo («01»), em destaque por cima do titulo. */
  index?: string;
  /** Etiqueta curta por cima do titulo («LOJA», «PERFIL»). */
  eyebrow?: ReactNode;
  title?: ReactNode;
  /** Botoes ou links alinhados a direita do titulo. */
  actions?: ReactNode;
  /** Filete de destaque no topo, como o rodape das paginas do manual. */
  ruled?: boolean;
  as?: 'section' | 'article' | 'div';
  /** Nivel do titulo na pagina; por defeito h3. */
  headingLevel?: 2 | 3 | 4;
  children?: ReactNode;
};

export function Card({
  index,
  eyebrow,
  title,
  actions,
  ruled,
  as: Tag = 'section',
  headingLevel = 3,
  className,
  children,
  ...props
}: CardProps) {
  const hasHeader = index || eyebrow || title || actions;
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';
  return (
    <Tag
      className={cx(
        'flex flex-col gap-4 border border-gu-line bg-gu-raised p-5 sm:p-6',
        ruled && 'border-t-rule border-t-gu-accent',
        className,
      )}
      {...props}
    >
      {hasHeader ? (
        // Num ecra estreito as acoes descem para baixo do titulo e um titulo
        // comprido (login, nome) parte em vez de alargar o cartao.
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            {index ? (
              <span className="font-display text-headline text-gu-accent">{index}</span>
            ) : null}
            {eyebrow ? (
              <span className="font-sub text-label font-bold uppercase text-gu-ink-muted">
                {eyebrow}
              </span>
            ) : null}
            {title ? (
              <Heading className="font-display text-headline uppercase text-gu-ink [overflow-wrap:anywhere]">
                {title}
              </Heading>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </Tag>
  );
}
